<template>
    <div class="edit-container">
        <!-- 添加TopBar组件 -->
        <TopBar :user="user" :apps="apps" />

        <!-- 页面头部 -->
        <div class="edit-header">
            <h1 class="edit-title">
                <i class="icon">✏️</i>
                编辑单词本
            </h1>
        </div>

        <!-- 主要内容区域 -->
        <div class="edit-main" v-if="!loading">
            <!-- 固定操作按钮 -->
            <div class="fixed-actions">
                <button class="add-word-btn" @click="showAddWordDialog" title="添加单词">
                    <i class="icon">+</i>
                </button>
                <button class="move-top-btn" @click="moveToTop" title="移到顶部">
                    <i class="icon">↑</i>
                </button>
                <button class="move-bottom-btn" @click="moveToBottom" title="移到底部">
                    <i class="icon">↓</i>
                </button>
                <button class="export-btn" @click="exportToExcel" title="导出为Excel">
                    <i class="icon">📤</i>
                </button>
                <button class="import-btn" @click="showImportDialog" title="从Excel导入">
                    <i class="icon">📥</i>
                </button>
            </div>

            <!-- 单词表标题 -->
            <div class="book-title-container">
                <div v-if="hasPreviousBook" @click="goToPreviousBook" class="nav-arrow">
                    <button class="side_btn">上一份单词表</button>
                </div>

                <div class="book-title">
                    <span>{{ bookName }}</span>
                </div>

                <div v-if="hasNextBook" @click="goToNextBook" class="nav-arrow">
                    <button class="side_btn">下一份单词表</button>
                </div>
            </div>

            <!-- 单词列表 -->
            <div class="words-table-container">
                <table class="words-table">
                    <thead>
                        <tr>
                            <th>序号</th>
                            <th>名称</th>
                            <th>注释</th>
                            <th>掌握情况</th>
                            <th>相关操作</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(word, index) in words" :key="word.fromNode_id">
                            <td class="counter">{{ index + 1 }}</td>
                            <td class="name editable" :contenteditable="isEditing" @dblclick="enableEdit($event)"
                                @blur="updateWord($event, index, 'name')" @focus="saveOriginalContent($event)">
                                {{ word.name }}
                            </td>
                            <td class="desc editable" :contenteditable="isEditing" @dblclick="enableEdit($event)"
                                @blur="updateWord($event, index, 'desc')" @focus="saveOriginalContent($event)">
                                {{ word.desc }}
                            </td>
                            <td class="strength">{{ word.strength }}</td>
                            <td class="actions">
                                <button class="action-btn" @click="readWord(word.name)" title="朗读">
                                    <i class="icon">🔊</i>
                                </button>
                                <button class="action-btn delete-btn" @click="deleteWord(index)" title="删除">
                                    <i class="icon">🗑️</i>
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <!-- 添加单词对话框 -->
        <div class="dialog-overlay" v-if="showAddDialog">
            <div class="dialog">
                <div class="dialog-header">
                    <h3>添加单词</h3>
                    <button class="close-btn" @click="closeAddWordDialog">×</button>
                </div>
                <div class="dialog-body">
                    <div class="form-group">
                        <label for="wordName">名称</label>
                        <input type="text" id="wordName" v-model="newWord.name" placeholder="请输入单词名称" />
                    </div>
                    <div class="form-group">
                        <label for="wordDesc">注释</label>
                        <input type="text" id="wordDesc" v-model="newWord.desc" placeholder="请输入单词注释"
                            @focus="translateWord" />
                    </div>
                </div>
                <div class="dialog-footer">
                    <button class="cancel-btn" @click="closeAddWordDialog">取消</button>
                    <button class="confirm-btn" @click="addWord">确定</button>
                </div>
            </div>
        </div>

        <!-- 导入对话框 -->
        <div class="dialog-overlay" v-if="showImportDialogFlag">
            <div class="dialog">
                <div class="dialog-header">
                    <h3>从Excel导入单词</h3>
                    <button class="close-btn" @click="closeImportDialog">×</button>
                </div>
                <div class="dialog-body">
                    <div class="form-group">
                        <label for="excelFile">选择Excel文件</label>
                        <input type="file" id="excelFile" @change="handleFileUpload" accept=".xlsx,.xls" />
                    </div>
                    <div class="import-instructions">
                        <p>请确保Excel文件格式如下：</p>
                        <ul>
                            <li>第一列为单词名称</li>
                            <li>第二列为单词注释</li>
                            <li>无需包含表头</li>
                        </ul>
                    </div>
                </div>
                <div class="dialog-footer">
                    <button class="cancel-btn" @click="closeImportDialog">取消</button>
                    <button class="confirm-btn" @click="importWords" :disabled="!excelData.length">导入</button>
                </div>
            </div>
        </div>

        <!-- 加载状态 -->
        <div v-if="loading" class="loading-overlay">
            <div class="loading-spinner">
                <div class="spinner"></div>
                <p>{{ loadingMessage }}</p>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, onMounted, reactive } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import * as XLSX from 'xlsx'
import TopBar from '@/components/TopBar.vue'
import AuthService from '@/services/authService.js'
import AbhsService from '@/services/abhsService.js'

export default {
    name: 'EditView',
    components: {
        TopBar
    },
    setup() {
        const route = useRoute()
        const router = useRouter()

        // 响应式数据
        const user = ref({
            name: '用户名',
            id: 1,
            userType: 1,
            avatar: require('@/assets/logo.png')
        })

        const apps = ref([])
        const loading = ref(false)
        const loadingMessage = ref('加载中...')
        const bookName = ref('')
        const words = ref([])
        const isEditing = ref(false)
        const originalContent = ref('')
        const showAddDialog = ref(false)
        const showImportDialogFlag = ref(false)
        const excelData = ref([])

        // 导航相关
        const hasPreviousBook = ref(false)
        const hasNextBook = ref(false)
        const bookList = ref([])
        const bookIndex = ref(0)

        const newWord = reactive({
            name: '',
            desc: ''
        })

        // 加载用户信息
        const loadUserInfo = () => {
            const currentUser = AuthService.getCurrentUser()
            if (currentUser) {
                user.value.name = currentUser.nickname || currentUser.username || '用户名'
                user.value.id = currentUser.userid || currentUser.id || 1
                user.value.userType = currentUser.userType || 1
                if (currentUser.icon) {
                    if (currentUser.icon.startsWith('/static/uploads/')) {
                        user.value.avatar = currentUser.icon
                    } else if (currentUser.icon.startsWith('uploads/')) {
                        user.value.avatar = `/static/${currentUser.icon}`
                    } else {
                        user.value.avatar = `/static/uploads/${currentUser.icon}`
                    }
                } else {
                    user.value.avatar = require('@/assets/logo.png')
                }
            }
        }

        // 获取单词表名称和单词列表
        const loadWordsData = async (bookNameParam = null) => {
            try {
                loading.value = true
                loadingMessage.value = '加载单词中...'

                // 从路由参数获取单词表名称
                const currentBookName = bookNameParam || route.query.bookName || '默认单词表'
                bookName.value = currentBookName
                console.log('获取单词表名称:', bookName.value)

                // 获取单词列表
                const response = await AbhsService.getWords(user.value.id, bookName.value)
                console.log('获取单词列表响应:', response)
                if (response.status === 200) {
                    // 确保数据是数组格式
                    let wordData = []
                    // 修复：从 response.message 获取数据而不是 response.data
                    if (Array.isArray(response.message)) {
                        wordData = response.message
                    } else if (response.data && Array.isArray(response.data)) {
                        wordData = response.data
                    } else if (response.message && typeof response.message === 'object') {
                        // 如果是对象，尝试获取其中的数组数据
                        wordData = response.message.data || response.message.words || []
                    } else if (response.data && typeof response.data === 'object') {
                        // 如果是对象，尝试获取其中的数组数据
                        wordData = response.data.data || response.data.words || []
                    } else {
                        wordData = []
                    }

                    // 确保创建一个新的数组来触发响应式更新
                    words.value = [...wordData]
                    console.log('设置单词列表:', words.value)
                    console.log('单词列表长度:', words.value.length)

                    // 验证数据结构
                    if (words.value.length > 0) {
                        console.log('第一个单词数据:', words.value[0])
                    }
                } else {
                    throw new Error(response.message || '获取单词列表失败')
                }
            } catch (error) {
                console.error('获取单词列表失败:', error)
                alert('获取单词列表失败: ' + error.message)
            } finally {
                loading.value = false
            }
        }

        // 加载单词表列表
        const loadBookList = async () => {
            try {
                const response = await AbhsService.getWordList(user.value.id)
                console.log('获取单词表列表:', response)
                if (response.status === 200) {
                    bookList.value = response.mybooks || []
                    // 找到当前单词表的索引
                    bookIndex.value = bookList.value.findIndex(book => book.name === bookName.value)
                    // 更新导航状态
                    updateNavigationStatus()
                } else {
                    console.error('获取单词表列表失败:', response.message)
                }
            } catch (error) {
                console.error('获取单词表列表出错:', error)
            }
        }

        // 更新导航状态（上一节/下一节按钮）
        const updateNavigationStatus = () => {
            hasPreviousBook.value = bookIndex.value > 0
            hasNextBook.value = bookIndex.value < bookList.value.length - 1
        }

        // 导航到上一个单词表
        const goToPreviousBook = () => {
            if (bookIndex.value > 0) {
                bookIndex.value--
                const previousBookName = bookList.value[bookIndex.value].name
                // 更新URL参数
                router.push({
                    query: {
                        ...route.query,
                        bookName: previousBookName
                    }
                })
                // 重新加载数据
                loadWordsData(previousBookName)
                updateNavigationStatus()
            }
        }

        // 导航到下一个单词表
        const goToNextBook = () => {
            if (bookIndex.value < bookList.value.length - 1) {
                bookIndex.value++
                const nextBookName = bookList.value[bookIndex.value].name
                // 更新URL参数
                router.push({
                    query: {
                        ...route.query,
                        bookName: nextBookName
                    }
                })
                // 重新加载数据
                loadWordsData(nextBookName)
                updateNavigationStatus()
            }
        }

        // 启用编辑模式
        const enableEdit = (event) => {
            isEditing.value = true
            event.target.setAttribute('contenteditable', true)
            event.target.focus()
        }

        // 保存原始内容
        const saveOriginalContent = (event) => {
            originalContent.value = event.target.textContent
        }

        // 更新单词
        const updateWord = async (event, index, field) => {
            if (!isEditing.value) return

            const newValue = event.target.textContent.trim()
            const oldValue = originalContent.value

            // 如果内容没有改变，直接返回
            if (newValue === oldValue) {
                isEditing.value = false
                return
            }

            // 如果是名称字段且为空，恢复原值
            if (field === 'name' && !newValue) {
                event.target.textContent = oldValue
                alert('单词名称不能为空')
                isEditing.value = false
                return
            }

            try {
                loading.value = true
                loadingMessage.value = '更新单词中...'

                // 获取单词ID
                const wordId = words.value[index].fromNode_id

                // 准备更新数据
                let wordName = words.value[index].name
                let wordDesc = words.value[index].desc

                if (field === 'name') {
                    wordName = newValue
                } else if (field === 'desc') {
                    wordDesc = newValue
                }

                // 调用API更新单词
                const response = await AbhsService.modifyWord(
                    user.value.id,
                    wordId,
                    wordName,
                    wordDesc
                )

                console.log('更新单词响应:', response)
                if (response.status === 200) {
                    // 更新本地数据
                    if (field === 'name') {
                        words.value[index].name = newValue
                    } else if (field === 'desc') {
                        words.value[index].desc = newValue
                    }
                    alert('更新成功')
                } else {
                    // 恢复原值
                    event.target.textContent = oldValue
                    throw new Error(response.message || '更新单词失败')
                }
            } catch (error) {
                // 恢复原值
                event.target.textContent = oldValue
                console.error('更新单词失败:', error)
                alert('更新单词失败: ' + (error.response?.data?.message || error.message || '未知错误'))
            } finally {
                isEditing.value = false
                loading.value = false
            }
        }

        // 删除单词
        const deleteWord = async (index) => {
            if (!confirm('确定要删除这个单词吗？')) {
                return
            }

            try {
                loading.value = true
                loadingMessage.value = '删除单词中...'

                // 获取要删除的单词ID
                const wordId = words.value[index].fromNode_id

                // 调用API删除单词
                const response = await AbhsService.deleteWord(
                    user.value.id,
                    bookName.value,
                    wordId
                )

                console.log('删除单词响应:', response)
                if (response.status === 200) {
                    // 从本地列表中删除
                    words.value.splice(index, 1)
                    alert('删除成功')
                } else {
                    throw new Error(response.message || '删除单词失败')
                }
            } catch (error) {
                console.error('删除单词失败:', error)
                alert('删除单词失败: ' + (error.response?.data?.message || error.message || '未知错误'))
            } finally {
                loading.value = false
            }
        }

        // 显示添加单词对话框
        const showAddWordDialog = () => {
            showAddDialog.value = true
            newWord.name = ''
            newWord.desc = ''
        }

        // 关闭添加单词对话框
        const closeAddWordDialog = () => {
            showAddDialog.value = false
        }

        // 添加单词
        const addWord = async () => {
            if (!newWord.name.trim() || !newWord.desc.trim()) {
                alert('请填写完整的单词信息')
                return
            }

            try {
                loading.value = true
                loadingMessage.value = '添加单词中...'

                // 调用API添加单词
                const response = await AbhsService.addWord(
                    user.value.id,
                    bookName.value,
                    newWord.name,
                    newWord.desc
                )

                console.log('添加单词响应:', response)
                if (response.status === 200) {
                    // 添加到本地列表
                    words.value.push({
                        fromNode_id: response.data?.id || response.message?.id || Date.now(), // 使用返回的ID或临时ID
                        name: newWord.name,
                        desc: newWord.desc,
                        strength: 0
                    })

                    // 关闭对话框
                    closeAddWordDialog()
                    alert('添加成功')
                } else {
                    throw new Error(response.message || '添加单词失败')
                }
            } catch (error) {
                console.error('添加单词失败:', error)
                alert('添加单词失败: ' + (error.response?.data?.message || error.message || '未知错误'))
            } finally {
                loading.value = false
            }
        }

        // 朗读单词
        const readWord = (wordName) => {
            // 检查浏览器是否支持语音合成
            if ('speechSynthesis' in window) {
                const utterance = new SpeechSynthesisUtterance(wordName)
                utterance.lang = 'en-US' // 设置语言为英语
                speechSynthesis.speak(utterance)
            } else {
                alert('您的浏览器不支持语音合成功能')
            }
        }

        // 翻译单词（中英文互译）
        const translateWord = async () => {
            // 如果用户在wordName输入框中输入了内容，并且wordDesc为空，则尝试获取翻译
            if (newWord.name && !newWord.desc) {
                try {
                    // 检查是否是中文输入（包含中文字符）
                    const isChinese = /[\u4e00-\u9fa5]/.test(newWord.name);

                    if (isChinese) {
                        // 如果是中文，尝试获取对应的英文翻译
                        console.log("检测到中文输入，尝试获取英文翻译");

                        // 调用后端API来获取翻译
                        const response = await AbhsService.translateWord(newWord.name);

                        if (response.status === 200) {
                            newWord.desc = response.message;
                            console.log("找到翻译结果:", response.message);
                        } else {
                            console.log("未找到翻译结果");
                        }
                    } else if (/^[A-Za-z]+$/.test(newWord.name)) {
                        // 如果是英文单词，尝试获取中文翻译
                        console.log("检测到英文输入，尝试获取中文翻译");

                        // 调用后端API来获取翻译
                        const response = await AbhsService.translateWord(newWord.name);

                        if (response.status === 200) {
                            newWord.desc = response.message;
                            console.log("找到翻译结果:", response.message);
                        } else {
                            console.log("未找到翻译结果");
                        }
                    }
                } catch (error) {
                    console.error("翻译过程中出错:", error);
                }
            }
        }

        // 移到顶部
        const moveToTop = () => {
            // 将单词列表反转，使底部的单词移到顶部
            words.value.reverse()
            alert('单词列表已反转')
        }

        // 移到底部
        const moveToBottom = () => {
            // 这个功能与移到顶部相同，因为我们是通过反转列表来实现的
            moveToTop()
        }

        // 导出为Excel
        const exportToExcel = () => {
            if (!words.value.length) {
                alert('没有数据可以导出')
                return
            }

            // 准备导出数据
            const exportData = words.value.map((word, index) => ({
                '序号': index + 1,
                '名称': word.name,
                '注释': word.desc,
                '掌握情况': word.strength
            }))

            // 创建工作表
            const ws = XLSX.utils.json_to_sheet(exportData)

            // 创建工作簿
            const wb = XLSX.utils.book_new()
            XLSX.utils.book_append_sheet(wb, ws, '单词表')

            // 导出文件
            XLSX.writeFile(wb, `${bookName.value}_单词表.xlsx`)
        }

        // 显示导入对话框
        const showImportDialog = () => {
            showImportDialogFlag.value = true
            excelData.value = []
        }

        // 关闭导入对话框
        const closeImportDialog = () => {
            showImportDialogFlag.value = false
            excelData.value = []
        }

        // 处理文件上传
        const handleFileUpload = (event) => {
            const file = event.target.files[0]
            if (!file) return

            const reader = new FileReader()
            reader.onload = (e) => {
                try {
                    const data = new Uint8Array(e.target.result)
                    const workbook = XLSX.read(data, { type: 'array' })

                    // 获取第一个工作表
                    const firstSheetName = workbook.SheetNames[0]
                    const worksheet = workbook.Sheets[firstSheetName]

                    // 将工作表转换为JSON
                    const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 })

                    // 过滤空行并转换为单词对象数组
                    excelData.value = jsonData
                        .filter(row => row.length >= 2 && (row[0] || row[1])) // 确保至少有两列且不全为空
                        .map(row => ({
                            name: row[0] || '',
                            desc: row[1] || ''
                        }))

                    console.log('解析的Excel数据:', excelData.value)
                } catch (error) {
                    console.error('解析Excel文件失败:', error)
                    alert('解析Excel文件失败: ' + error.message)
                }
            }
            reader.readAsArrayBuffer(file)
        }

        // 导入单词
        const importWords = async () => {
            if (!excelData.value.length) {
                alert('没有数据可以导入')
                return
            }

            try {
                loading.value = true
                loadingMessage.value = `导入单词中... (0/${excelData.value.length})`

                let successCount = 0
                const total = excelData.value.length

                // 逐个添加单词
                for (let i = 0; i < excelData.value.length; i++) {
                    const word = excelData.value[i]

                    if (!word.name.trim() || !word.desc.trim()) {
                        console.warn('跳过空行:', word)
                        continue
                    }

                    try {
                        // 调用API添加单词
                        const response = await AbhsService.addWord(
                            user.value.id,
                            bookName.value,
                            word.name,
                            word.desc
                        )

                        if (response.status === 200) {
                            // 添加到本地列表
                            words.value.push({
                                fromNode_id: response.data?.id || response.message?.id || Date.now(),
                                name: word.name,
                                desc: word.desc,
                                strength: 0
                            })
                            successCount++
                        } else {
                            console.error('添加单词失败:', word, response.message)
                        }
                    } catch (error) {
                        console.error('添加单词失败:', word, error)
                    }

                    // 更新进度
                    loadingMessage.value = `导入单词中... (${successCount}/${total})`
                }

                alert(`导入完成！成功导入 ${successCount} 个单词`)
                closeImportDialog()
            } catch (error) {
                console.error('导入单词失败:', error)
                alert('导入单词失败: ' + error.message)
            } finally {
                loading.value = false
            }
        }

        // 初始化应用
        const initApp = async () => {
            try {
                console.log('编辑单词本页面初始化中...')

                // 加载用户信息
                loadUserInfo()

                // 加载单词数据
                await loadWordsData()

                // 获取单词表列表用于导航
                await loadBookList()

                console.log('编辑单词本页面初始化完成')
            } catch (error) {
                console.error('编辑单词本页面初始化失败:', error)
            } finally {
                loading.value = false
            }
        }

        // 组件挂载时初始化
        onMounted(() => {
            console.log('Edit.vue 组件挂载')
            initApp()
        })

        return {
            user,
            apps,
            loading,
            loadingMessage,
            bookName,
            words,
            isEditing,
            originalContent,
            showAddDialog,
            showImportDialogFlag,
            excelData,
            newWord,
            enableEdit,
            saveOriginalContent,
            updateWord,
            deleteWord,
            showAddWordDialog,
            closeAddWordDialog,
            addWord,
            readWord,
            translateWord,
            moveToTop,
            moveToBottom,
            exportToExcel,
            showImportDialog,
            closeImportDialog,
            handleFileUpload,
            importWords,
            // 导航相关
            hasPreviousBook,
            hasNextBook,
            goToPreviousBook,
            goToNextBook
        }
    }
}
</script>

<style scoped>
.edit-container {
    min-height: 100vh;
    background-color: #f5f5f5;
}

.edit-header {
    background-color: #fff;
    padding: 20px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.edit-title {
    margin: 0;
    font-size: 24px;
    color: #333;
}

.edit-main {
    padding: 20px;
    max-width: 1200px;
    margin: 0 auto;
}

.fixed-actions {
    position: fixed;
    bottom: 20px;
    right: 20px;
    z-index: 100;
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.add-word-btn,
.move-top-btn,
.move-bottom-btn,
.export-btn,
.import-btn {
    background-color: #4CAF50;
    color: white;
    border: none;
    border-radius: 50%;
    width: 50px;
    height: 50px;
    font-size: 18px;
    cursor: pointer;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
}

.move-top-btn {
    background-color: #2196F3;
}

.move-bottom-btn {
    background-color: #2196F3;
}

.export-btn {
    background-color: #FF9800;
}

.import-btn {
    background-color: #9C27B0;
}

.add-word-btn:hover {
    background-color: #45a049;
}

.move-top-btn:hover,
.move-bottom-btn:hover {
    background-color: #1976D2;
}

.export-btn:hover {
    background-color: #F57C00;
}

.import-btn:hover {
    background-color: #7B1FA2;
}

.book-title-container {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 20px;
    margin-bottom: 20px;
}

.book-title {
    font-size: 20px;
    font-weight: bold;
    color: #333;
    text-align: center;
}

.nav-arrow {
    cursor: pointer;
}

.side_btn {
    height: 30px;
    border-radius: 10%;
    background: #42b983;
    color: white;
    border: none;
    font-size: 16px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    padding: 0 15px;
}

.side_btn:hover {
    background: #359c6d;
}

.words-table-container {
    overflow-x: auto;
    background-color: #fff;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.words-table {
    width: 100%;
    border-collapse: collapse;
}

.words-table th,
.words-table td {
    padding: 12px 15px;
    text-align: left;
    border-bottom: 1px solid #ddd;
}

.words-table th {
    background-color: #f8f9fa;
    font-weight: bold;
    color: #555;
}

.words-table tbody tr:hover {
    background-color: #f5f5f5;
}

.editable {
    cursor: pointer;
    padding: 5px;
    border-radius: 4px;
}

.editable:hover {
    background-color: #e9ecef;
}

.editable:focus {
    outline: 2px solid #007bff;
    background-color: #fff;
}

.actions {
    display: flex;
    gap: 10px;
}

.action-btn {
    background: none;
    border: none;
    cursor: pointer;
    font-size: 16px;
    padding: 5px;
    border-radius: 4px;
}

.action-btn:hover {
    background-color: #e9ecef;
}

.delete-btn:hover {
    background-color: #f8d7da;
    color: #721c24;
}

.dialog-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
}

.dialog {
    background-color: #fff;
    border-radius: 8px;
    width: 90%;
    max-width: 500px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.dialog-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px;
    border-bottom: 1px solid #eee;
}

.dialog-header h3 {
    margin: 0;
    color: #333;
}

.close-btn {
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
    color: #999;
}

.close-btn:hover {
    color: #333;
}

.dialog-body {
    padding: 20px;
}

.form-group {
    margin-bottom: 20px;
}

.form-group label {
    display: block;
    margin-bottom: 5px;
    font-weight: bold;
    color: #555;
}

.form-group input {
    width: 100%;
    padding: 10px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 16px;
    box-sizing: border-box;
}

.form-group input:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.import-instructions {
    background-color: #f8f9fa;
    padding: 15px;
    border-radius: 4px;
    margin-top: 15px;
}

.import-instructions ul {
    margin: 10px 0;
    padding-left: 20px;
}

.import-instructions li {
    margin-bottom: 5px;
}

.dialog-footer {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    padding: 20px;
    border-top: 1px solid #eee;
}

.cancel-btn,
.confirm-btn {
    padding: 10px 20px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 16px;
}

.cancel-btn {
    background-color: #6c757d;
    color: white;
}

.cancel-btn:hover {
    background-color: #5a6268;
}

.confirm-btn {
    background-color: #007bff;
    color: white;
}

.confirm-btn:hover {
    background-color: #0069d9;
}

.confirm-btn:disabled {
    background-color: #ccc;
    cursor: not-allowed;
}

.loading-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2000;
}

.loading-spinner {
    text-align: center;
    color: white;
}

.spinner {
    border: 4px solid rgba(255, 255, 255, 0.3);
    border-radius: 50%;
    border-top: 4px solid white;
    width: 40px;
    height: 40px;
    animation: spin 1s linear infinite;
    margin: 0 auto 10px;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

@media (max-width: 768px) {
    .edit-main {
        padding: 10px;
    }

    .words-table th,
    .words-table td {
        padding: 8px 10px;
        font-size: 14px;
    }

    .fixed-actions {
        bottom: 15px;
        right: 15px;
    }

    .add-word-btn,
    .move-top-btn,
    .move-bottom-btn,
    .export-btn,
    .import-btn {
        width: 45px;
        height: 45px;
        font-size: 16px;
    }

    .book-title-container {
        flex-direction: column;
        gap: 10px;
    }

    .side_btn {
        font-size: 14px;
        padding: 0 10px;
    }
}
</style>